/**
 * PickerNavigator — the shared single-column drilldown navigator embedded
 * by every picker dialog (Open File, Save As, Link Folder, Drive picker).
 *
 * Replaces the per-dialog recursive tree views (issue #21). Layout: a picker
 * over the whole drive has Places down its full left side (./pickerPlaces);
 * beside it the folder panel, top to bottom: a toolbar row (Back / Forward /
 * Up, the clickable breadcrumb path, and the search box), then a flat
 * current-folder list — or the parent + current folders side by side. The dialog
 * wrappers keep their own modal chrome + action footer and own the
 * `selectedId` state; this component only drives navigation, listing, and
 * search.
 *
 * Search hits the server's recursive `/api/files/search` endpoint, which
 * permission-filters the *entire* permitted subtree. It searches everywhere
 * the picker reaches; a chip above the hits narrows it to the open folder.
 *
 * Theming: rendered inside dialogs that already apply the theme class on
 * their modal element, so this uses only `themed-*` utility classes and
 * does NOT call `useTheme()` itself (see CLAUDE.md).
 */
import {
  useState, useEffect, useId, useCallback, useRef, useMemo, useSyncExternalStore,
  forwardRef, useImperativeHandle, type FocusEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject,
} from 'react'
import { flushSync } from 'react-dom'
import { ChevronRight, ArrowLeft, ArrowRight, ArrowUp, Search, Loader2, MoreHorizontal, X, Check, ListChecks, ListX } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from '@papan/i18n'

import { filesApi, type FileListItem } from '@papan/files/client'
import { PICKER_LIST_LIMIT, fetchAndCacheList, fetchAndCacheSearch, fileKeys, reaskOnEmailChange } from '@papan/files/client/queries'
import { FileIcon, fileIconProps, miniappIconProps, isMouseHover } from '@papan/core-ui/ui'
import { sortFileItems, isRootFolderId } from './picker-utils'
import { isPendingPointer } from './pending-pointer'
import { LOCATION_SEPARATOR, readableName } from './locations'
import { SearchScopeControl } from './home/SearchScopeControl'
import { PickerRowButton, forgetFirstClick, isSecondClickOnSelf, noteFirstClick } from './PickerRowButton'
import { MutedEndings, nameParts } from './nameParts'
import { PlaceListView, PlacesNav, listPlaceOf, listsNeededBy, placeCrumb, usePickerPlaces, type PickerPlace, type PlaceEntry } from './pickerPlaces'
// Re-export the pure helpers so `@papan/files/ui/picker` is a one-stop import
// (no barrel side effects) for consumers that need a drive root id.
export { getRootFolderId, isRootFolderId, sortFileItems } from './picker-utils'
/** Crumbs carry real folder names; show them to people through this. */
export { readableName } from './locations'

/** A picker path as a person reads it — "Projects › Q3 Report". */
export function crumbsText(crumbs: readonly PickerCrumb[]): string {
  return crumbs.map(crumb => readableName(crumb.name)).join(LOCATION_SEPARATOR)
}

/** See `PickerNavigatorProps.decorateItem`. */
export interface RowDecoration {
  readOnly?: boolean
  /** Dim the row and say why on hover. */
  dimReason?: string
}

/** See `PickerNavigatorProps.rowChoice`. */
export type RowChoice = true | false | string

/**
 * `checkStyle: 'row'`: how a click chose a row — the desktop's own
 * conventions. `only`: a plain click (this row alone). `toggle`: with Ctrl
 * (Cmd on a Mac), added or taken out. `range`: with Shift, every choosable
 * row from the last one clicked to this one, in `range`; `range-add` is Shift
 * with Ctrl/Cmd — that range added to what is chosen. `checkStyle: 'box'`
 * uses `range-add` alone: a Shift-click on a row or its tick adds the range.
 */
export interface RowGesture {
  how: 'only' | 'toggle' | 'range' | 'range-add'
  range?: PickerSelection[]
}

export interface PickerRoot {
  /** Folder id this root browses (papan root, a workspace folder, a linked folder). */
  id: string
  /** Display label — shown in the root chooser and as the breadcrumb base. */
  label: string
  /** Linked-folder root — rendered with the link icon. */
  isLink?: boolean
}

export interface PickerCrumb { id: string; name: string }

export interface PickerSelection {
  id: string
  name: string
  isFolder: boolean
  parentId: string | null
  /** The raw list/search row the selection came from — lets wrappers read
   *  per-row fields (e.g. SaveAsDialog's read-only check) without a refetch. */
  raw: FileListItem
}

export interface PickerNavigatorHandle {
  /** Folder currently being browsed (deepest breadcrumb crumb); null on the root chooser. */
  currentFolderId: string | null
  /** Imperatively jump to a folder — rebuilds the breadcrumb stack. Used by
   *  SaveAsDialog's shortcut buttons and its read-only auto-jump. */
  navigateTo: (folderId: string) => void
  /** A folder's path as this picker tells it (the path `navigateTo` would show). */
  pathOf: (folderId: string) => Promise<PickerCrumb[]>
  /** The open folder once its listing has answered (and `onNavigate` has reported it); null while it loads. */
  readonly loadedFolderId: string | null
}

export interface PickerNavigatorProps {
  /** One or more roots. Multiple → a root chooser is shown as level 0. */
  roots: PickerRoot[]
  /** Folder to open on mount. A deep folder under a root is resolved via breadcrumbs. */
  initialFolderId?: string | null
  /** Which rows are selectable. Folders always drill in on click. */
  mode: 'file' | 'folder' | 'fileOrFolder'
  /** Controlled selection — the wrapper owns it. Highlights the matching row. */
  selectedId: string | null
  /** Fired when a selectable file row is clicked. */
  onSelect: (item: PickerSelection) => void
  /** Fired on double-click of a selectable file row. */
  onConfirm?: (item: PickerSelection) => void
  /** Fired after every drilldown / back / breadcrumb jump once the folder
   *  has loaded. `parentPermission` is the viewer's permission on it.
   *  `folderId` is null when the navigator shows a list that is not a folder
   *  (a place such as Projects or Shared): there is nothing to pick or save
   *  into until an entry is opened. */
  onNavigate?: (folderId: string | null, breadcrumbs: PickerCrumb[], parentPermission?: string) => void
  /** Hide rows from both listing and search (e.g. '.workspace.json', excludeIds). */
  hideItem?: (item: FileListItem) => boolean
  /** Per-row decoration — e.g. SaveAsDialog's read-only lock badge, or a
   *  reason the row is dimmed (the #605 save picker's "can't save here"). */
  decorateItem?: (item: FileListItem, parentPermission?: string) => RowDecoration | undefined
  /** The rows the browse view is showing (the open folder's, and its parent's
   *  in split view), whenever they change — for a wrapper whose decoration
   *  needs a lookup per row (the #605 save picker's save targets). */
  onItemsShown?: (items: FileListItem[]) => void
  /**
   * CHECKBOX selection, owned by the wrapper (the #605 handoff picker). With
   * both set, rows `rowChoice` allows get a checkbox: clicking a FILE row ticks
   * it, a FOLDER row still opens, and its checkbox chooses it without opening
   * it. The ticks are the selection — `selectedId` highlights nothing here.
   */
  checkedIds?: ReadonlySet<string>
  /** `gesture`: how the click chose (see `RowGesture`) — absent for a plain
   *  tick; `checkStyle: 'box'` sends only `range-add` (a Shift-click). */
  onToggleCheck?: (item: PickerSelection, gesture?: RowGesture) => void
  /**
   * How a choice is made in checkbox mode. `'box'` (default): a tick beside
   * each row, as above. `'row'`: no ticks — a click on any row `rowChoice`
   * allows chooses it (toggles it, when several may be chosen), folders
   * included; such a folder opens on double-click or on its own open button
   * beside the row (a touch screen and a keyboard have no double-click). A
   * folder that may not be chosen still opens on a click.
   */
  checkStyle?: 'box' | 'row'
  /**
   * `checkStyle: 'row'`: a choosable folder is being OPENED by a double-click
   * — whose first click chose (or unchose) it. Called before it opens, so the
   * wrapper can undo that click: the person was going in, not choosing.
   */
  onOpenByDoubleClick?: (item: PickerSelection) => void
  /**
   * The open folder's listing failed (the network, a hiccup): the list says
   * so, with a Try again, instead of reading as empty — and the wrapper hears
   * it, so nothing still acts on the folder it came from (a save must not go
   * to the folder the person left while this one shows).
   */
  onNavigateFailed?: () => void
  /**
   * Ticks (`checkStyle: 'box'`): a Select all / Deselect all control at the
   * toolbar's right end, over the list on screen (the open folder, or the
   * search results) — every row that may be chosen. `select` false when all
   * of them are ticked already (it reads "Deselect all" then).
   */
  onToggleAll?: (items: PickerSelection[], select: boolean) => void
  /** The most that may be ticked: Select all is off when it would pass it. */
  maxChecked?: number
  /**
   * Whether a row may be CHOSEN (checkbox mode). `true`: yes. `false`: no, and
   * nothing to explain (a folder in a files-only pick — it just opens). A
   * string: no, and why — the row is dimmed and says so. Folders stay openable
   * whatever this returns. Default: whatever `mode` allows.
   */
  rowChoice?: (item: FileListItem, parentPermission?: string) => RowChoice
  /** Root ids to search. Defaults to every root's id. */
  searchRootIds?: string[]
  /** Focus the search box once the picker opens. Default true; SaveAsDialog turns it
   *  off so its filename input keeps focus. Skipped when the search sits atop a
   *  collapsed Places column (the picker opened inside a folder). */
  autoFocusSearch?: boolean
  /** Two-column layout: the left pane shows the parent folder's contents,
   *  the right pane the current folder's. Only kicks in once you've drilled
   *  at least one level deep — at a root (no parent) it falls back to a
   *  single column. Search and the root chooser stay full-width. A picker over
   *  the whole drive ALWAYS gets this layout (Places + parent + current), so
   *  every drive picker looks and works the same; this prop only decides it for
   *  pickers scoped to one folder. */
  splitView?: boolean
  className?: string
}

/**
 * A folder's `getBreadcrumbs` chain (which excludes the papan drive root)
 * anchored at the picker root it passes through, or — for a navigator over
 * the drive root — under that root.
 */
function anchorAtRoot(folderId: string, crumbs: PickerCrumb[], roots: PickerRoot[]): PickerCrumb[] {
  const rootIdx = crumbs.findIndex(c => roots.some(r => r.id === c.id))
  if (rootIdx >= 0) {
    const root = roots.find(r => r.id === crumbs[rootIdx].id)!
    return [{ id: root.id, name: root.label }, ...crumbs.slice(rootIdx + 1)]
  }
  // No specific root on the ancestor chain. `getBreadcrumbs` excludes
  // the papan drive root folder, so if one of our roots IS that drive
  // root it's the implicit ancestor — anchor there. Otherwise fall back
  // to the first root (best effort).
  const base = roots.find(r => isRootFolderId(r.id)) ?? roots[0]
  // Dev warn on the truly-no-anchor case: the breadcrumb resolved but
  // doesn't pass through any declared root AND no root is a papan drive
  // root. Unreachable from the current dialogs, but if it ever fires
  // the breadcrumb would mis-anchor — surface it loudly in dev.
  if (crumbs.length > 0 && !roots.some(r => isRootFolderId(r.id)) && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[PickerNavigator] buildStackFor(${folderId}): breadcrumb does not pass through any declared root; falling back to roots[0]=${roots[0]?.id}`,
    )
  }
  if (base) return [{ id: base.id, name: base.label }, ...crumbs]
  return crumbs
}

const SM_UP = '(min-width: 640px)'

function subscribeSmUp(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {}
  const query = window.matchMedia(SM_UP)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}
const isSmUp = () => typeof window.matchMedia !== 'function' || window.matchMedia(SM_UP).matches
const isSmUpOnServer = () => true

/** Whether the viewport is at least Tailwind's `sm` — where Places is a column rather than a row. */
function useSmUp(): boolean {
  return useSyncExternalStore(subscribeSmUp, isSmUp, isSmUpOnServer)
}

/**
 * The search field. Atop a collapsed Places column only its magnifier shows
 * (`collapsedHidden` hides the rest): pressing anywhere on the box focuses the
 * field, and the focus opens the column. Clearing keeps the focus, so the
 * column stays open to type again.
 */
function SearchBox({ inputRef, value, onChange, onFocus, onBlur, onLeave, className, collapsedHidden = '', tone = 'themed-bg-tertiary' }: {
  inputRef: RefObject<HTMLInputElement | null>
  value: string
  onChange: (value: string) => void
  onFocus: () => void
  onBlur: (e: FocusEvent<HTMLInputElement>) => void
  /** Leave the field (Escape on an empty query), keeping focus inside the picker. */
  onLeave: () => void
  className?: string
  collapsedHidden?: string
  /** The field's background (bare atop a collapsed Places column until it opens). */
  tone?: string
}) {
  const { t } = useTranslation('files')
  return (
    <div
      data-testid="picker-search"
      // Not a <label>: it would take over the field's accessible name from its placeholder.
      onMouseDown={e => {
        if (e.target === inputRef.current || (e.target as Element).closest('button')) return
        e.preventDefault()
        inputRef.current?.focus()
      }}
      // On a phone the field's text is 16px (index.css — less and iOS zooms
      // in on focus): the box around it stays tight.
      className={`flex items-center gap-1.5 min-w-0 ${tone} rounded px-2 py-1 max-md:py-0.5 cursor-text ${className ?? ''}`}
    >
      <Search size={13} className="themed-text-muted shrink-0" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        // Escape clears the query, then leaves the field — which closes a Places
        // column the focus was holding open over the toolbar.
        onKeyDown={e => {
          // Not while an input method is composing: there Escape cancels the composition.
          if (e.key !== 'Escape' || e.nativeEvent.isComposing) return
          e.preventDefault()
          e.stopPropagation()
          if (value) onChange('')
          else onLeave()
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={t('drivePicker.search')}
        // A phone's field is 16px (iOS zooms in on less); its placeholder need not be.
        className={`flex-1 min-w-0 text-xs bg-transparent themed-text outline-none placeholder:themed-text-muted max-md:placeholder:text-xs ${collapsedHidden}`}
      />
      {value && (
        <Tooltip label={t('drivePicker.clearSearch')}>
          <button
            type="button"
            // Keep the focus in the field: blurring it first would drop a short query and this button with it.
            onMouseDown={e => e.preventDefault()}
            onClick={() => { onChange(''); inputRef.current?.focus() }}
            aria-label={t('drivePicker.clearSearch')}
            className="shrink-0 themed-text-muted hover:themed-text"
          >
            <X size={13} />
          </button>
        </Tooltip>
      )}
    </div>
  )
}

/** A row's name as the drive's rows show it: the full name, its endings muted. */
function RowName({ item }: { item: FileListItem }) {
  return <MutedEndings parts={nameParts({ name: item.name, isFolder: item.isFolder, fileType: item.fileType })} />
}

function sameCrumbs(a: readonly PickerCrumb[], b: readonly PickerCrumb[]): boolean {
  return a.length === b.length && a.every((crumb, i) => crumb.id === b[i].id)
}

/** A place entry as the folder row `hideItem` judges — its identity and kind are real, the rest is blank. */
function placeEntryItem(entry: PlaceEntry): FileListItem {
  return {
    id: entry.folderId, name: entry.name, isFolder: true, parentId: null,
    contentHash: null, size: 0, mimeType: null, createdAt: '', updatedAt: '',
  }
}

/** A row as the wrappers receive it. */
function selectionOf(item: FileListItem): PickerSelection {
  return { id: item.id, name: item.name, isFolder: item.isFolder, parentId: item.parentId, raw: item }
}

/** Checkbox mode, threaded to every list that renders rows. */
interface ColumnChecks {
  checkedIds: ReadonlySet<string>
  choiceOf: (item: FileListItem, parentPermission?: string) => RowChoice
  onToggle: (item: FileListItem, parentPermission?: string) => void
  /** `checkStyle: 'row'` — the row itself is the choice; no tick. */
  rows: boolean
}

/** Which rows a mode shows: `folder` hides files; others show everything
 *  (folders are still needed for drilling even in `file` mode). */
function modeShows(isFolder: boolean, mode: PickerNavigatorProps['mode']): boolean {
  return mode === 'folder' ? isFolder : true
}

/**
 * #1210 — a picker read asked once more on an org-email change (`reaskOnEmailChange`), the re-ask past the cache: the
 * picker has no realtime channel, so the new identity's cached entry may be stale (`fresh` exists for that).
 */
export function readFreshOnReask<T>(fresh: boolean, read: (fresh: boolean) => Promise<T>): Promise<T> {
  let asked = 0
  return reaskOnEmailChange(() => read(fresh || asked++ > 0))
}

export const PickerNavigator = forwardRef<PickerNavigatorHandle, PickerNavigatorProps>(
  function PickerNavigator(props, ref) {
    const { roots, initialFolderId, mode, selectedId, className, autoFocusSearch = true, splitView = false } = props
    const { t } = useTranslation('files')
    const queryClient = useQueryClient()
    /**
     * Folders (and searches) already re-read ONCE in this dialog instance.
     *
     * `fresh` exists because a picker has no realtime channel: it mounts, reads
     * the shared cache, and copies the result into local state, so a row deleted
     * moments ago is still offered. But forcing it on EVERY navigation makes
     * Back/Forward and drilling back up — the common repeat pattern in a picker
     * — a network call each time, and this component renders a failed load as
     * "This folder is empty" with no error surface, so a transient blip would
     * show a populated folder as empty far more often.
     *
     * First visit per MOUNT is the balance: the dialog opens on current data
     * (the reported bug), and moving around inside it stays cached. DriveView
     * mounts the dialog only while open, so a reopen is a fresh mount.
     *
     * ── StrictMode: TWO independent protections, keep both ──
     *
     * A "first visit" flag is exactly the shape React's double-invoke consumes.
     * Two properties each prevent it here, and either ALONE is sufficient —
     * measured by rendering four variants under StrictMode:
     *
     *   (1) every `takeFresh` call sits BELOW its effect's early return, so the
     *       mount invocation returns before reaching it; and
     *   (2) every key is derived from the value that effect guards on, so even a
     *       hoisted call consumes `list:null` / `search:<rid>:` — keys no real
     *       load uses.
     *
     * guarded+derived, hoisted+derived, and guarded+mount-stable all yield
     * `fresh: true` on every real load. ONLY hoisting AND making a key
     * mount-stable breaks it — and the conjunction is PER CALL SITE, not per
     * component: doing both to the browse site alone breaks browse while the
     * parent column and search stay correct. Then the throwaway run burns the key: the real
     * load asks with the 30s window, and `fetchQuery` evaluates staleness BEFORE
     * its in-flight dedup, so it resolves from the STALE value while the fresh
     * fetch is still running and the first run's result is dropped as cancelled.
     * Dead in dev, alive in prod.
     *
     * So neither property is decorative and neither is the whole story. Two
     * earlier versions of this comment each named one of them as THE mechanism;
     * both were wrong, and the second inverted its own advice by blessing the
     * hoist while warning about the key.
     *
     * No test renders under StrictMode, so none of this is enforced.
     *
     * ── WHAT A REVISIT GETS ──
     *
     * A revisit reads with `fresh: false`, and whether that is stale depends on
     * things OUTSIDE this component: `patchListsFromFileEvent` reaches its cache
     * entry by prefix when a `useFiles` consumer is mounted, and
     * `WorkspaceExplorer` separately invalidates the same prefix for every event
     * parent. Both are usually present and neither is guaranteed — coverage
     * varies with which decks are mounted, with the event's shape (a
     * `file:deleted` without `parentId` patches nothing, and most publishers omit
     * it), and at the drive root with a key mismatch.
     *
     * That map is deliberately NOT reproduced here. Four review rounds each
     * found the version in this comment wrong in a different way — the hosts, the
     * mobile case, the persistence scope, the publisher list, the causal claim —
     * because it is an encyclopedia of two other subsystems that changes without
     * this file. Read `queries.ts`'s patcher and `WorkspaceExplorer` for the
     * current truth.
     *
     * The fix does not rest on it: first-visit freshness makes the dialog OPEN on
     * current data whether or not anything corrected the entry meanwhile, which
     * is the property the reported bug needed.
     */
    const freshlyLoadedRef = useRef<Set<string>>(new Set())
    const takeFresh = useCallback((key: string): boolean => {
      if (freshlyLoadedRef.current.has(key)) return false
      freshlyLoadedRef.current.add(key)
      return true
    }, [])

    // Callback / array props are usually inline in the wrappers — keep them
    // in a ref so the data-fetching effects don't re-run on every render.
    const propsRef = useRef(props)
    propsRef.current = props

    // ─── Navigation state ─────────────────────────────────────────────
    // Browser-style history: `history` is a list of breadcrumb-stack
    // snapshots, `index` points at the current one. A "new navigation"
    // (drill / sibling / jump / up / imperative) truncates anything ahead
    // of `index` and appends; Back / Forward just move `index`. Each stack
    // is the breadcrumb path — `stack[0]` is always a root, an empty stack
    // is the root chooser (only reachable when roots.length > 1).
    const [nav, setNav] = useState<{ history: PickerCrumb[][]; index: number }>({ history: [], index: -1 })
    const stackReady = nav.index >= 0
    const stack = stackReady ? nav.history[nav.index] : []
    const [searchResults, setSearchResults] = useState<FileListItem[] | null>(null)
    // Each search hit's parent folder as a root-anchored path (null: couldn't resolve).
    const [resultPaths, setResultPaths] = useState<Map<string, PickerCrumb[] | null>>(new Map())
    const resultPathsRef = useRef(resultPaths)
    resultPathsRef.current = resultPaths
    // Places (./pickerPlaces): the Drive menu inside a picker over the whole
    // drive. A path someone clicks through stays where they found it; one the
    // picker works out is told from where they are or else from its place; a
    // list place (Projects, PDApps, Shared) is a location with no folder. The
    // lists behind those places are read only when a path shown here needs them.
    const lastListsNeeded = useRef({ entities: false, shares: false })
    const listsNeeded = useMemo(() => {
      const hits = searchResults ?? []
      // The open path needs a list only for its list place and entry (a path
      // clicked through is not re-placed); search hits' paths need theirs to be placed.
      // A hit inside the path the person is on is told from that path — no list needed.
      const onPath = (path: readonly PickerCrumb[]) =>
        path.some(crumb => !isRootFolderId(crumb.id) && stack.some(open => open.id === crumb.id))
      const hitPaths = hits.map(hit => (hit.parentId ? resultPaths.get(hit.parentId) : null)).filter(path => !path || !onPath(path))
      const needs = listsNeededBy([listPlaceOf(stack[0]?.id) ? stack.slice(0, 2) : [], ...hitPaths])
      // While new hits' paths are still resolving, keep what was needed rather
      // than switching a list off and straight back on.
      const resolving = hits.some(hit => hit.parentId && !resultPaths.has(hit.parentId))
      return resolving
        ? { entities: needs.entities || lastListsNeeded.current.entities, shares: needs.shares || lastListsNeeded.current.shares }
        : needs
    }, [stack, searchResults, resultPaths])
    lastListsNeeded.current = listsNeeded
    const { places, rebase, placeHolds, activeKey, driveRoot, labelled } = usePickerPlaces(roots, listsNeeded)
    const labelledRef = useRef(labelled)
    labelledRef.current = labelled
    // What the trail and headers show: the stored path, the root named as it is now.
    const shownStack = useMemo(() => labelled(stack), [labelled, stack])
    const rebaseRef = useRef(rebase)
    rebaseRef.current = rebase
    const activeKeyRef = useRef(activeKey)
    activeKeyRef.current = activeKey
    const driveRootRef = useRef(driveRoot)
    driveRootRef.current = driveRoot
    const smUp = useSmUp()
    const listPlace = listPlaceOf(stack[stack.length - 1]?.id)
    const currentFolderId = stack.length > 0 && !listPlace ? stack[stack.length - 1].id : null
    const canGoBack = nav.index > 0
    const canGoForward = nav.index >= 0 && nav.index < nav.history.length - 1
    const stackRef = useRef(stack)
    stackRef.current = stack

    // ─── Browse state ─────────────────────────────────────────────────
    const [items, setItems] = useState<FileListItem[]>([])
    const [parentPermission, setParentPermission] = useState<string | undefined>(undefined)
    // The folder whose listing has answered, with its permission — the only one
    // `onNavigate` may report. One ref, set together, so the two cannot disagree.
    const loadedFolderRef = useRef<{ folderId: string; permission: string | undefined } | null>(null)
    // The path last reported for it, so a new path to the same folder is reported too.
    const reportedPathRef = useRef<PickerCrumb[] | null>(null)
    const [browseLoading, setBrowseLoading] = useState(true)
    const [browseFailed, setBrowseFailed] = useState(false)
    /** The open folder holds more than the listing brought (it stops at 500):
     *  "all" on screen would not be all of it. */
    const [browsePartial, setBrowsePartial] = useState(false)
    const [browseAgain, setBrowseAgain] = useState(0)

    // Parent pane — the parent folder's contents. Only loaded while the pane
    // shows (see `showParentPane`) and its parent is a real folder.
    const [parentColItems, setParentColItems] = useState<FileListItem[]>([])
    const [parentColPermission, setParentColPermission] = useState<string | undefined>(undefined)
    const [parentColLoading, setParentColLoading] = useState(false)
    // One layout for every picker over the whole drive: Places, the parent
    // column and the current folder. A scoped picker opts in with `splitView`.
    // (On a phone a drive picker has no parent column: Places is a row there,
    // and the path's ↑ does its job. It is not rendered at all, so not loaded.)
    const showParentPane = stack.length >= 2 && (places.length > 0 ? smUp : splitView)
    // Inside a project/app/share opened from its place, the parent is that
    // place's LIST (siblings = the other projects), not a folder to load.
    const parentListPlace = showParentPane ? listPlaceOf(stack[stack.length - 2].id) : null
    const parentFolderId = showParentPane && !parentListPlace ? stack[stack.length - 2].id : null
    // Search sits atop Places, where Places is a column (wide screens); on a
    // phone, or in a picker without Places, it stays in the toolbar.
    const searchInRail = smUp && places.length > 0

    // ─── Search state ─────────────────────────────────────────────────
    const [searchInput, setSearchInput] = useState('')
    const [searchLoading, setSearchLoading] = useState(false)
    /** The search stopped short of every match (see `browsePartial`). */
    const [searchPartial, setSearchPartial] = useState(false)
    /** "At most N" shown under Select all after a press it refused. */
    const [overNoteShown, setOverNoteShown] = useState(false)
    const overNoteId = useId()
    useEffect(() => {
      if (!overNoteShown) return
      const timer = setTimeout(() => setOverNoteShown(false), 3000)
      return () => clearTimeout(timer)
    }, [overNoteShown])
    const isSearching = searchInput.trim().length >= 2
    const searchInputRef = useRef<HTMLInputElement>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    // On a phone (Places a row of chips) the toolbar has no room for a field
    // beside the path: search is an icon, and tapping it swaps the path for the
    // field and a Cancel. Open while tapped open or holding a query.
    const compactSearch = !searchInRail && !smUp
    const [searchOpen, setSearchOpen] = useState(false)
    const searchExpanded = compactSearch && (searchOpen || searchInput !== '')
    const openSearch = useCallback(() => {
      // Focused inside the tap itself — iOS opens the keyboard only then.
      flushSync(() => setSearchOpen(true))
      searchInputRef.current?.focus()
    }, [])
    const closeSearch = useCallback(() => {
      setSearchInput('')
      setSearchOpen(false)
      rootRef.current?.focus()
    }, [])
    const leaveSearch = useCallback(() => {
      setSearchOpen(false)
      rootRef.current?.focus()
    }, [])

    // Search scope. EVERYWHERE by default: someone searching a picker usually
    // doesn't know where the thing is (#650). It narrows to the open folder on
    // request, and only while that folder stays open — moving elsewhere goes
    // back to everywhere. Nothing to narrow to at a list place, or when the
    // open folder already is everything the picker searches.
    const [narrowedTo, setNarrowedTo] = useState<string | null>(null)
    const searchRootIds = props.searchRootIds ?? roots.map(root => root.id)
    // Named as the trail shows it (the ids are the stored path's).
    const scopeFolder = currentFolderId && !(searchRootIds.length === 1 && searchRootIds[0] === currentFolderId)
      ? shownStack[shownStack.length - 1]
      : null
    const searchScopeId = scopeFolder && narrowedTo === scopeFolder.id ? scopeFolder.id : null

    const [overflowOpen, setOverflowOpen] = useState(false)
    // Breadcrumb "click the empty area to reveal the full /a/b/c path".
    const [pathExpanded, setPathExpanded] = useState(false)

    // Resolve the breadcrumb stack for an arbitrary folder id: a root is a
    // single crumb (no fetch); anything else is its ancestor chain anchored
    // at a root (see `anchorAtRoot`). `placedPath` tells it as the person should see it.
    const buildStackFor = useCallback(async (folderId: string): Promise<PickerCrumb[]> => {
      const rs = propsRef.current.roots
      const asRoot = rs.find(r => r.id === folderId)
      if (asRoot) return [{ id: asRoot.id, name: asRoot.label }]

      let crumbs: PickerCrumb[] = []
      try {
        crumbs = await filesApi.getBreadcrumbs(folderId)
      } catch {
        crumbs = []
      }
      return anchorAtRoot(folderId, crumbs, rs)
    }, [])

    // ─── Mount: seed the stack ────────────────────────────────────────
    useEffect(() => {
      let cancelled = false
      ;(async () => {
        let initStack: PickerCrumb[]
        if (initialFolderId) {
          initStack = await buildStackFor(initialFolderId)
        } else if (roots.length === 1) {
          initStack = [{ id: roots[0].id, name: roots[0].label }]
        } else {
          initStack = []
        }
        if (!cancelled) setNav({ history: [rebaseRef.current(initStack)], index: 0 })
      })()
      return () => { cancelled = true }
      // Mount only — initialFolderId / roots are start-of-life inputs.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ─── Browse: load the current folder ──────────────────────────────
    useEffect(() => {
      if (!stackReady) return
      loadedFolderRef.current = null
      if (currentFolderId == null) {
        setItems([])
        setParentPermission(undefined)
        setBrowseLoading(false)
        if (listPlace) propsRef.current.onNavigate?.(null, stackRef.current)
        return
      }
      let cancelled = false
      setBrowseLoading(true)
      setBrowseFailed(false)
      // FRESH. A picker that offers a file which was deleted a moment ago sends
      // the user into a failure they cannot see the cause of — and unlike the
      // drive, this dialog has no realtime channel correcting it: it mounts,
      // reads the cache once, and copies the result into local state.
      // #1210: an org-email change mid-read is asked again once, past the cache (never an empty or failed pane for it)
      readFreshOnReask(takeFresh(`list:${currentFolderId}`), fresh => fetchAndCacheList(queryClient, currentFolderId, undefined, { fresh }))
        .then(res => {
          if (cancelled) return
          setItems(res.files)
          setParentPermission(res.parentPermission)
          // A full page means "there may be more" even when the response says
          // otherwise: one row hidden from this person makes the route report
          // `hasMore: false` over a folder of 700.
          setBrowsePartial(res.hasMore === true || res.files.length >= PICKER_LIST_LIMIT)
          loadedFolderRef.current = { folderId: currentFolderId, permission: res.parentPermission }
          reportedPathRef.current = stackRef.current
          propsRef.current.onNavigate?.(currentFolderId, labelledRef.current(stackRef.current), res.parentPermission)
        })
        .catch(() => {
          if (cancelled) return
          setItems([])
          setParentPermission(undefined)
          setBrowseFailed(true)
          loadedFolderRef.current = null
          propsRef.current.onNavigateFailed?.()
        })
        .finally(() => { if (!cancelled) setBrowseLoading(false) })
      return () => { cancelled = true }
      // `listPlace`: moving from one list place to another is a new location too.
      // `browseAgain`: the person's Try again on a listing that failed.
    }, [currentFolderId, listPlace, stackReady, queryClient, browseAgain])

    // ─── Split view: load the parent folder (left pane) ───────────────
    useEffect(() => {
      if (!parentFolderId) {
        setParentColItems([])
        setParentColPermission(undefined)
        setParentColLoading(false)
        return
      }
      let cancelled = false
      setParentColLoading(true)
      // The left pane is selectable too (SaveAs / FolderPicker use split view),
      // so it needs the same first-visit freshness as the main column.
      readFreshOnReask(takeFresh(`list:${parentFolderId}`), fresh => fetchAndCacheList(queryClient, parentFolderId, undefined, { fresh }))
        .then(res => {
          if (cancelled) return
          setParentColItems(res.files)
          setParentColPermission(res.parentPermission)
        })
        .catch(() => {
          if (cancelled) return
          setParentColItems([])
          setParentColPermission(undefined)
        })
        .finally(() => { if (!cancelled) setParentColLoading(false) })
      return () => { cancelled = true }
    }, [parentFolderId, queryClient])

    // ─── Search: debounced recursive query, everywhere or in the open folder ────
    useEffect(() => {
      const q = searchInput.trim()
      if (q.length < 2) {
        setSearchResults(null)
        setSearchLoading(false)
        return
      }
      setSearchLoading(true)
      // A slower, older query (other text, other scope) must not land last.
      let cancelled = false
      const timer = setTimeout(async () => {
        const { roots: rs, searchRootIds: rootsToSearch, hideItem, mode: m } = propsRef.current
        const rootIds = searchScopeId ? [searchScopeId] : rootsToSearch ?? rs.map(r => r.id)
        try {
          const responses = await Promise.all(rootIds.map(rid =>
            readFreshOnReask(takeFresh(`search:${rid}:${q}`), fresh => fetchAndCacheSearch(queryClient, q, { rootId: rid }, { fresh }))
              .catch(() => ({ files: [] as FileListItem[], total: 0, truncated: false })),
          ))
          if (!cancelled) setSearchPartial(responses.some(r => r.truncated === true))
          const seen = new Set<string>()
          const merged: FileListItem[] = []
          for (const r of responses) {
            for (const f of r.files) {
              if (seen.has(f.id)) continue
              if (hideItem?.(f)) continue
              if (!modeShows(f.isFolder, m)) continue
              seen.add(f.id)
              merged.push(f)
            }
          }
          if (!cancelled) setSearchResults(sortFileItems(merged))
        } catch {
          if (!cancelled) setSearchResults([])
        } finally {
          if (!cancelled) setSearchLoading(false)
        }
      }, 250)
      return () => {
        cancelled = true
        clearTimeout(timer)
      }
    }, [searchInput, searchScopeId, queryClient])

    // ─── Search: resolve full paths for result rows ───────────────────
    useEffect(() => {
      if (!searchResults || searchResults.length === 0) return
      let cancelled = false
      const parentIds = [...new Set(
        searchResults.map(r => r.parentId).filter((p): p is string => !!p),
      )].filter(pid => !resultPathsRef.current.has(pid))
      if (parentIds.length === 0) return
      ;(async () => {
        const entries = await Promise.all(parentIds.map(async pid => {
          try {
            const crumbs = await queryClient.fetchQuery({
              queryKey: fileKeys.breadcrumbs(pid),
              queryFn: () => filesApi.getBreadcrumbs(pid),
              staleTime: 60_000,
            })
            return [pid, anchorAtRoot(pid, crumbs, propsRef.current.roots)] as const
          } catch {
            return [pid, null] as const
          }
        }))
        if (cancelled) return
        setResultPaths(prev => {
          const next = new Map(prev)
          for (const [pid, path] of entries) next.set(pid, path)
          return next
        })
      })()
      return () => { cancelled = true }
    }, [searchResults, queryClient])

    // Whether the field has focus — kept when the window crosses the `sm` width
    // and the field moves between the toolbar and Places (it remounts there).
    const searchFocusedRef = useRef(false)
    const onSearchFocus = useCallback(() => { searchFocusedRef.current = true }, [])
    const placesCollapsed = searchInRail && showParentPane && !isSearching
    const onSearchBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
      const field = e.currentTarget
      // Settled after the commit: a blur from the field being replaced is not the person leaving it.
      setTimeout(() => {
        // Replaced (it moved), or focused again already (e.g. by Clear): not left.
        if (searchInputRef.current !== field || document.activeElement === field) return
        searchFocusedRef.current = false
        // A phone's field left empty goes back to its icon.
        if (compactSearch && field.value === '') setSearchOpen(false)
        // In a collapsed Places column a query too short to search would sit
        // unseen once the column closes: leaving the field (not the window)
        // drops it — nothing was searched for.
        if (placesCollapsed && document.hasFocus()) setSearchInput(query => (query.trim().length < 2 ? '' : query))
      })
    }, [placesCollapsed, compactSearch])
    useEffect(() => {
      if (!searchFocusedRef.current) return
      // Crossing into phone width mid-search: the field stays open (not
      // folded into its icon under the person's typing), and keeps the focus.
      if (compactSearch) setSearchOpen(true)
      searchInputRef.current?.focus()
    }, [searchInRail]) // on the move only
    useEffect(() => {
      if (searchExpanded && searchFocusedRef.current) searchInputRef.current?.focus()
    }, [searchExpanded])

    useEffect(() => forgetFirstClick, [])

    // Focus the search once the picker knows where it opened — but not inside a
    // collapsed Places column, which would spring open over the parent column.
    const focusedOnOpenRef = useRef(false)
    useEffect(() => {
      if (!stackReady || focusedOnOpenRef.current) return
      focusedOnOpenRef.current = true
      if (autoFocusSearch && !(searchInRail && showParentPane)) searchInputRef.current?.focus()
    }, [stackReady, autoFocusSearch, searchInRail, showParentPane])

    // A path filed under a list place before that list loaded, which the list
    // turns out not to show (e.g. a project only admin view reveals): tell it
    // again from where it is stored, in place — it is the same location.
    useEffect(() => {
      if (!stackReady || placeHolds(stack)) return
      const folderId = stack[stack.length - 1].id
      let cancelled = false
      buildStackFor(folderId).then(stored => {
        if (cancelled || stored[stored.length - 1]?.id !== folderId || stackRef.current !== stack) return
        const retold = rebaseRef.current(stored)
        setNav(prev => {
          if (prev.history[prev.index] !== stack) return prev
          const history = [...prev.history]
          history[prev.index] = retold
          return { ...prev, history }
        })
        // (Wrappers hear the re-told path from the effect below.)
      })
      return () => { cancelled = true }
    }, [stackReady, stack, placeHolds, buildStackFor])

    // The same loaded folder reached by another path (a place clicked, Back or
    // Forward between two paths to it, a re-told path): wrappers show its path
    // too, so report it again. A pending listing reports the new path itself;
    // a label that merely loads (the drive's name) is not a new path.
    useEffect(() => {
      const loaded = loadedFolderRef.current
      if (!stackReady || !loaded || loaded.folderId !== currentFolderId) return
      if (reportedPathRef.current && sameCrumbs(reportedPathRef.current, stack)) return
      reportedPathRef.current = stack
      propsRef.current.onNavigate?.(loaded.folderId, labelledRef.current(stack), loaded.permission)
    }, [stackReady, stack, currentFolderId])

    // Collapse the transient breadcrumb UI (expanded full-path, overflow
    // menu), and widen the search again, whenever the location changes.
    useEffect(() => {
      setPathExpanded(false)
      setOverflowOpen(false)
      setNarrowedTo(null)
    }, [currentFolderId])

    // ─── Navigation actions ───────────────────────────────────────────
    // `navigate` is the single "go to a new location" primitive: truncate
    // any forward history, append the new stack. Back / Forward just move
    // the history pointer; Up is a fresh navigation to the parent. A path the
    // person clicks through is stored as it is — it stays where they found it;
    // `openFolder` works out a path the picker has to find itself.
    // Gone somewhere (a hit, a Places chip, Back, Up): the search is over —
    // its query dropped, and on a phone the path comes back in place of the
    // field. Said at the move itself, not read from where focus went (iOS
    // keeps it in the field through a tap on a button).
    const endSearch = useCallback(() => {
      setSearchInput('')
      setSearchOpen(false)
    }, [])

    const navigate = useCallback((placed: PickerCrumb[]) => {
      endSearch()
      setNav(prev => {
        // The same location again (a double-clicked place) is no new step for Back.
        if (prev.index >= 0 && sameCrumbs(prev.history[prev.index], placed)) return prev
        const base = prev.index >= 0 ? prev.history.slice(0, prev.index + 1) : []
        const history = [...base, placed]
        return { history, index: history.length - 1 }
      })
    }, [])

    const back = useCallback(() => {
      endSearch()
      setNav(prev => (prev.index > 0 ? { ...prev, index: prev.index - 1 } : prev))
    }, [])

    const forward = useCallback(() => {
      endSearch()
      setNav(prev => (prev.index < prev.history.length - 1 ? { ...prev, index: prev.index + 1 } : prev))
    }, [])

    // Up — go to the parent of the current folder (a fresh navigation). A
    // place is a top-level location: Up from it leads to the drive root. (A
    // root of a multi-root picker still goes up to the root chooser.)
    const goUp = useCallback(() => {
      endSearch()
      const root = driveRootRef.current
      const isPlace = activeKeyRef.current
      setNav(prev => {
        if (prev.index < 0) return prev
        const cur = prev.history[prev.index]
        if (cur.length === 0) return prev
        const up = cur.length === 1 && root && isPlace(cur) ? [root] : cur.slice(0, -1)
        const base = prev.history.slice(0, prev.index + 1)
        const history = [...base, up]
        return { history, index: history.length - 1 }
      })
    }, [])

    // D24: a `.chain` that Needs approval is followed by nobody — browsing into it would
    // only show an EMPTY folder (the server lists nothing through it). Its row is drawn
    // disabled with the reason (`pendingDimReason`) and never opens or is chosen.
    const drillInto = useCallback((folder: FileListItem) => {
      if (isPendingPointer(folder)) return
      navigate([...stackRef.current, { id: folder.id, name: folder.name }])
    }, [navigate])

    // Split-view left-pane folder click — swap the current folder for a
    // sibling, keeping the same parent so the left pane stays put.
    const selectSibling = useCallback((folder: FileListItem) => {
      if (isPendingPointer(folder)) return
      const cur = stackRef.current
      if (cur.length >= 1) navigate([...cur.slice(0, -1), { id: folder.id, name: folder.name }])
    }, [navigate])

    const jumpToCrumb = useCallback((index: number) => {
      setOverflowOpen(false)
      navigate(stackRef.current.slice(0, index + 1))
    }, [navigate])

    const chooseRoot = useCallback((root: PickerRoot) => {
      navigate([{ id: root.id, name: root.label }])
    }, [navigate])

    // A folder's path told from where the person is (see `rebase`), in the stored form.
    const placedPath = useCallback(
      async (folderId: string) => rebaseRef.current(await buildStackFor(folderId), stackRef.current),
      [buildStackFor],
    )
    // …and as the trail would show it (the handle's `pathOf`).
    const pathOf = useCallback(
      async (folderId: string) => labelledRef.current(await placedPath(folderId)),
      [placedPath],
    )
    // Open a folder by id (a search hit, a shortcut): told from where the person is.
    const openFolder = useCallback(async (folderId: string) => {
      navigate(await placedPath(folderId))
    }, [navigate, placedPath])

    const navigateTo = useCallback((folderId: string) => { void openFolder(folderId) }, [openFolder])

    useImperativeHandle(ref, () => ({
      currentFolderId,
      navigateTo,
      pathOf,
      get loadedFolderId() { return loadedFolderRef.current?.folderId ?? null },
    }), [currentFolderId, navigateTo, pathOf])

    const openPlace = useCallback((place: PickerPlace) => {
      navigate(place.folderId ? [{ id: place.folderId, name: place.label }] : [placeCrumb(place)])
    }, [navigate])
    // A place entry is a folder the wrapper may hide, like any listed row (e.g. the folder being moved).
    const hideEntry = useCallback((entry: PlaceEntry) => !!propsRef.current.hideItem?.(placeEntryItem(entry)), [])
    const openPlaceEntry = useCallback((entry: PlaceEntry) => {
      navigate([...stackRef.current, { id: entry.folderId, name: entry.name }])
    }, [navigate])
    // Parent-column list: switch to a sibling entry, keeping the place as the parent.
    const selectPlaceSibling = useCallback((entry: PlaceEntry) => {
      const cur = stackRef.current
      if (cur.length >= 1) navigate([...cur.slice(0, -1), { id: entry.folderId, name: entry.name }])
    }, [navigate])

    // ─── Row interactions ─────────────────────────────────────────────
    // The modifier keys of the click being handled, and the row a Shift range
    // runs from (see `chooseRow`); the lists a range may run through.
    const clickModsRef = useRef({ add: false, range: false })
    const anchorRef = useRef<string | null>(null)
    const listsRef = useRef<FileListItem[][]>([])
    const noteClick = useCallback((e: ReactMouseEvent) => {
      noteFirstClick(e)
      clickModsRef.current = { add: e.ctrlKey || e.metaKey, range: e.shiftKey }
    }, [])
    // Checkbox mode (the handoff picker): the wrapper owns a set of ticks.
    const checking = !!(props.checkedIds && props.onToggleCheck)
    const choiceOf = useCallback((item: FileListItem, parentPerm?: string): RowChoice => {
      const { rowChoice, mode: m } = propsRef.current
      if (isPendingPointer(item)) return t('driveView.chain.needsApproval')
      if (rowChoice) return rowChoice(item, parentPerm)
      return item.isFolder ? m !== 'file' : m !== 'folder'
    }, [t])
    /** Every choosable row from the anchor (the last row chosen without
     *  Shift) to `item`, in the on-screen list holding both — or null. */
    const rangeTo = useCallback((item: FileListItem, parentPerm?: string): PickerSelection[] | null => {
      const anchorId = anchorRef.current
      if (!anchorId || anchorId === item.id) return null
      const list = listsRef.current.find(l => l.some(r => r.id === anchorId) && l.some(r => r.id === item.id))
      if (!list) return null
      const a = list.findIndex(r => r.id === anchorId)
      const b = list.findIndex(r => r.id === item.id)
      return list.slice(Math.min(a, b), Math.max(a, b) + 1)
        .filter(r => choiceOf(r, parentPerm) === true).map(selectionOf)
    }, [choiceOf])
    /** A tick (`checkStyle: 'box'`): adds or takes out that row — or, with
     *  Shift, adds every choosable row from the anchor to it. */
    const toggleCheck = useCallback((item: FileListItem, parentPerm?: string) => {
      if (choiceOf(item, parentPerm) !== true) return
      const range = clickModsRef.current.range ? rangeTo(item, parentPerm) : null
      if (range) {
        propsRef.current.onToggleCheck?.(selectionOf(item), { how: 'range-add', range })
        return
      }
      anchorRef.current = item.id
      propsRef.current.onToggleCheck?.(selectionOf(item))
    }, [choiceOf, rangeTo])
    /** `checkStyle: 'row'`, and this row may be chosen: a click chooses it —
     *  a folder included, which then opens only on double-click or its open button. */
    const rowChooses = useCallback((item: FileListItem, parentPerm?: string) => {
      const { checkedIds, onToggleCheck, checkStyle } = propsRef.current
      return !!(checkedIds && onToggleCheck) && checkStyle === 'row' && choiceOf(item, parentPerm) === true
    }, [choiceOf])

    /**
     * `checkStyle: 'row'`: choose `item` the way the click asked — its
     * modifier keys, noted on the way in (`noteClick`). A Shift range runs, in
     * the list the row is in, from the last row chosen without Shift (the
     * anchor) to this one, taking only rows that may be chosen.
     */
    const chooseRow = useCallback((item: FileListItem, parentPerm?: string) => {
      const { add, range } = clickModsRef.current
      const span = range ? rangeTo(item, parentPerm) : null
      const gesture: RowGesture = span
        ? { how: add ? 'range-add' : 'range', range: span }
        : { how: add ? 'toggle' : 'only' }
      if (!span) anchorRef.current = item.id
      propsRef.current.onToggleCheck?.(selectionOf(item), gesture)
    }, [rangeTo])

    /** Ticks: a Shift-click on a choosable folder takes the range to it, as on
     *  a file — "Shift-click to select a range" — rather than open it. Only
     *  when there IS a range (an anchor in the same list): otherwise it opens,
     *  as a folder row does. */
    const shiftTakesRange = useCallback((item: FileListItem, parentPerm?: string) =>
      item.isFolder && clickModsRef.current.range
      && !!propsRef.current.checkedIds && !!propsRef.current.onToggleCheck && propsRef.current.checkStyle !== 'row'
      && choiceOf(item, parentPerm) === true && rangeTo(item, parentPerm) !== null,
    [choiceOf, rangeTo])

    const handleRowClick = useCallback((item: FileListItem, parentPerm?: string) => {
      if (isPendingPointer(item)) return
      if (rowChooses(item, parentPerm)) {
        chooseRow(item, parentPerm)
      } else if (shiftTakesRange(item, parentPerm)) {
        toggleCheck(item, parentPerm)
      } else if (item.isFolder) {
        drillInto(item)
      } else if (propsRef.current.checkedIds && propsRef.current.onToggleCheck) {
        toggleCheck(item, parentPerm)
      } else if (propsRef.current.mode !== 'folder') {
        propsRef.current.onSelect(selectionOf(item))
      }
    }, [drillInto, toggleCheck, rowChooses, chooseRow, shiftTakesRange])

    const handleRowDoubleClick = useCallback((item: FileListItem, parentPerm?: string) => {
      if (isPendingPointer(item)) return
      if (item.isFolder) {
        // A folder a click chooses opens on a double-click; any other folder's
        // single click already drilled in.
        if (rowChooses(item, parentPerm)) {
          propsRef.current.onOpenByDoubleClick?.(selectionOf(item))
          drillInto(item)
        }
        return
      }
      if (propsRef.current.checkedIds && propsRef.current.onToggleCheck) {
        if (choiceOf(item, parentPerm) === true) propsRef.current.onConfirm?.(selectionOf(item))
      } else if (propsRef.current.mode !== 'folder') {
        propsRef.current.onConfirm?.(selectionOf(item))
      }
    }, [choiceOf, rowChooses, drillInto])

    const handleSearchResultClick = useCallback(async (item: FileListItem) => {
      if (isPendingPointer(item)) return
      if (rowChooses(item)) {
        chooseRow(item)
        return
      }
      if (shiftTakesRange(item)) {
        toggleCheck(item)
        return
      }
      if (item.isFolder) {
        await openFolder(item.id)
        return
      }
      // Checkbox mode ticks a hit where it is: the results stay, so several
      // can be ticked from one search.
      if (propsRef.current.checkedIds && propsRef.current.onToggleCheck) {
        toggleCheck(item)
        return
      }
      // File hit — navigate to its parent for context, then select. The
      // navigate triggers an async state update + folder load; `onSelect`
      // fires synchronously here, so a wrapper reading `selectedFolderId`
      // (e.g. SaveAsDialog) sees the OLD folder until `onNavigate` fires
      // after the parent loads. Wrappers that care reconcile via
      // `selection.parentId` (SaveAsDialog does this in `handleSelect`).
      if (item.parentId) await openFolder(item.parentId)
      else endSearch()
      if (propsRef.current.mode !== 'folder') {
        propsRef.current.onSelect(selectionOf(item))
      }
    }, [openFolder, toggleCheck, rowChooses, chooseRow, shiftTakesRange])

    // ─── Derived view data ────────────────────────────────────────────
    const filterAndSort = useCallback((list: FileListItem[]) => {
      const { hideItem } = propsRef.current
      return sortFileItems(list.filter(f => (!hideItem || !hideItem(f)) && modeShows(f.isFolder, mode)))
    }, [mode])
    const visibleItems = useMemo(() => filterAndSort(items), [items, filterAndSort])
    const visibleParentItems = useMemo(() => filterAndSort(parentColItems), [parentColItems, filterAndSort])
    // Only what is ON SCREEN: while searching, the results — the open folder's
    // listing is still computed underneath, and a range through it would take
    // rows the person never saw.
    listsRef.current = isSearching ? [searchResults ?? []] : [visibleItems, visibleParentItems]
    useEffect(() => {
      propsRef.current.onItemsShown?.([...visibleItems, ...visibleParentItems])
    }, [visibleItems, visibleParentItems])
    // A hit's location reads like the path its folder opens at.
    const resultLocations = useMemo(() => {
      const locations = new Map<string, string>()
      for (const [parentId, path] of resultPaths) {
        // Told as clicking the hit would open it: from where the person is.
        if (path) locations.set(parentId, labelled(rebase(path, stack)).map(crumb => readableName(crumb.name)).join(LOCATION_SEPARATOR))
      }
      return locations
    }, [resultPaths, rebase, labelled, stack])

    const onRootChooser = stackReady && stack.length === 0
    const canGoUp = stack.length > 1
      || (stack.length === 1 && (roots.length > 1 || (!!driveRoot && activeKey(stack) !== null)))

    const navBtn = 'shrink-0 p-1 rounded themed-text-secondary hover:themed-text hover:themed-bg-tertiary transition-colors disabled:opacity-30 disabled:cursor-default'

    // Select all / Deselect all — ticks only, over the list on screen. Never
    // while that list is still the LAST folder's (or search's) under a spinner,
    // nor over a list cut short: "all" would tick rows the person never saw, or
    // claim a whole folder chosen when it is not.
    const toggleAll = (() => {
      const { onToggleAll, checkedIds, checkStyle, maxChecked } = props
      if (!onToggleAll || !checkedIds || checkStyle === 'row' || onRootChooser) return null
      if (isSearching ? searchLoading || searchPartial : browseLoading || browseFailed || browsePartial) return null
      const list = isSearching ? searchResults ?? [] : visibleItems
      const perm = isSearching ? undefined : parentPermission
      const choosable = list.filter(item => choiceOf(item, perm) === true)
      if (choosable.length === 0) return null
      const allTicked = choosable.every(item => checkedIds.has(item.id))
      const adding = choosable.filter(item => !checkedIds.has(item.id)).length
      const over = !allTicked && maxChecked !== undefined && checkedIds.size + adding > maxChecked
      const label = allTicked ? t('drivePicker.deselectAll') : t('drivePicker.selectAll')
      return {
        label,
        hint: over ? t('drivePicker.selectAllOver', { max: maxChecked }) : label,
        allTicked,
        disabled: over,
        run: () => {
          // The next Shift-click starts here, not from a row clicked in a
          // folder since left — a range from a stale anchor silently re-ticks
          // what Deselect all just cleared.
          anchorRef.current = null
          onToggleAll(choosable.map(selectionOf), !allTicked)
        },
      }
    })()
    // Over the most allowed, the control stays FOCUSABLE (`aria-disabled`, not
    // `disabled`) and says why — on a tap too, where there is no hover tooltip.
    const toggleAllButton = toggleAll && (
      <span className="relative shrink-0 flex">
        <Tooltip label={toggleAll.hint}>
          <button
            type="button"
            data-testid="picker-toggle-all"
            onClick={() => {
              if (!toggleAll.disabled) { toggleAll.run(); return }
              setOverNoteShown(true)
            }}
            aria-disabled={toggleAll.disabled || undefined}
            aria-label={compactSearch ? toggleAll.label : undefined}
            aria-describedby={toggleAll.disabled ? overNoteId : undefined}
            className={compactSearch
              ? `${navBtn} aria-disabled:opacity-30 aria-disabled:cursor-default`
              : 'shrink-0 px-2 py-1 rounded text-xs themed-text-secondary hover:themed-text hover:themed-bg-tertiary transition-colors aria-disabled:opacity-40 aria-disabled:cursor-default'}
          >
            {compactSearch
              ? (toggleAll.allTicked ? <ListX size={16} /> : <ListChecks size={16} />)
              : toggleAll.label}
          </button>
        </Tooltip>
        {toggleAll.disabled && (
          <span
            id={overNoteId}
            role="status"
            data-testid="picker-toggle-all-over"
            className={overNoteShown
              ? 'absolute right-0 top-full mt-1 z-20 w-max max-w-[16rem] rounded px-2 py-1 text-xs themed-text themed-bg-secondary border themed-border shadow'
              : 'sr-only'}
          >
            {toggleAll.hint}
          </span>
        )}
      </span>
    )
    const checks: ColumnChecks | undefined = checking
      ? { checkedIds: props.checkedIds!, choiceOf, onToggle: toggleCheck, rows: props.checkStyle === 'row' }
      : undefined

    return (
      // Places runs the full height; the folder panel beside it owns the
      // toolbar, so Back / Forward / Up sit with the folders they move through.
      // Focusable (not tabbable): where the search field hands focus back on Escape.
      <div ref={rootRef} tabIndex={-1} className={`flex min-h-0 outline-none ${className ?? ''}`} onClickCapture={noteClick}>
        {places.length > 0 && (
          <PlacesNav
            layout="column"
            places={places}
            activeKey={activeKey(stack)}
            onChoose={openPlace}
            // Expands (moving the columns aside) only when results replace them;
            // while the field is being typed in, focus holds it open over them.
            expanded={!showParentPane || isSearching}
            search={searchInRail
              ? (collapsedHidden, fieldTone) => (
                <SearchBox
                  inputRef={searchInputRef}
                  value={searchInput}
                  onChange={setSearchInput}
                  onFocus={onSearchFocus}
                  onBlur={onSearchBlur}
                  onLeave={leaveSearch}
                  className="flex-1"
                  collapsedHidden={collapsedHidden}
                  tone={fieldTone}
                />
              )
              : undefined}
          />
        )}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          {/* Toolbar: Back / Forward / Up · breadcrumb path · search — one row */}
          {searchExpanded ? (
          <div className="h-10 shrink-0 flex items-center gap-2 px-2 border-b themed-border">
            <SearchBox
              inputRef={searchInputRef}
              value={searchInput}
              onChange={setSearchInput}
              onFocus={onSearchFocus}
              onBlur={onSearchBlur}
              onLeave={leaveSearch}
              className="flex-1"
            />
            {toggleAllButton}
            <button type="button" onClick={closeSearch} className="shrink-0 px-1 text-xs themed-text-secondary hover:themed-text">
              {t('drivePicker.cancelSearch')}
            </button>
          </div>
          ) : (
          <div className="h-10 shrink-0 flex items-center gap-1 px-2 border-b themed-border">
            <Tooltip label={t('drivePicker.back')}>
              <button type="button" onClick={back} disabled={!canGoBack} aria-label={t('drivePicker.back')} className={navBtn}>
                <ArrowLeft size={15} />
              </button>
            </Tooltip>
            <Tooltip label={t('drivePicker.forward')}>
              <button type="button" onClick={forward} disabled={!canGoForward} aria-label={t('drivePicker.forward')} className={navBtn}>
                <ArrowRight size={15} />
              </button>
            </Tooltip>
            <Tooltip label={t('drivePicker.up')}>
              <button type="button" onClick={goUp} disabled={!canGoUp} aria-label={t('drivePicker.up')} className={navBtn}>
                <ArrowUp size={15} />
              </button>
            </Tooltip>

            {/* Breadcrumb path — fills the middle */}
            <div className="flex-1 min-w-0 mx-1 text-xs">
              {onRootChooser ? (
                <span className="themed-text-muted">{t('drivePicker.selectLocation')}</span>
              ) : (
                <Breadcrumbs
                  stack={shownStack}
                  expanded={pathExpanded}
                  onToggleExpanded={() => setPathExpanded(v => !v)}
                  overflowOpen={overflowOpen}
                  setOverflowOpen={setOverflowOpen}
                  onJump={jumpToCrumb}
                />
              )}
            </div>

            {toggleAllButton}

            {/* Search — right side, unless it sits atop Places; an icon on a phone */}
            {compactSearch ? (
              <Tooltip label={t('drivePicker.search')}>
                <button type="button" onClick={openSearch} aria-label={t('drivePicker.search')} className={navBtn}>
                  <Search size={15} />
                </button>
              </Tooltip>
            ) : !searchInRail && (
              <SearchBox
                inputRef={searchInputRef}
                value={searchInput}
                onChange={setSearchInput}
                onFocus={onSearchFocus}
                onBlur={onSearchBlur}
                onLeave={leaveSearch}
                className="shrink-0 w-[170px]"
              />
            )}
          </div>
          )}

          {places.length > 0 && (
            <PlacesNav layout="row" places={places} activeKey={activeKey(stack)} onChoose={openPlace} />
          )}
          {/* List / search results — single column, or parent | current
              split once we've drilled in (search + root chooser stay full
              width). */}
          {isSearching ? (
            <div className="flex-1 overflow-auto p-1.5 min-h-0" data-testid="picker-search-results">
              <div className="flex px-2 pt-0.5 pb-1.5 text-xs themed-text-muted">
                <SearchScopeControl
                  scope={{
                    folderName: scopeFolder ? readableName(scopeFolder.name) : null,
                    scoped: searchScopeId !== null,
                    onToggle: () => setNarrowedTo(searchScopeId ? null : scopeFolder?.id ?? null),
                  }}
                />
              </div>
              <SearchResultsView
                loading={searchLoading}
                results={searchResults}
                resultPaths={resultLocations}
                selectedId={selectedId}
                checks={checks}
                onClickResult={handleSearchResultClick}
                onOpenResult={(item) => { if (!isPendingPointer(item)) void openFolder(item.id) }}
              />
            </div>
          ) : onRootChooser ? (
            <div className="flex-1 overflow-auto p-1.5 min-h-0">
              <RootChooserView roots={roots} onChoose={chooseRoot} />
            </div>
          ) : listPlace ? (
            <div className="flex-1 overflow-auto p-1.5 min-h-0">
              <PlaceListView place={listPlace} selectedId={selectedId} hideEntry={hideEntry} onOpen={openPlaceEntry} />
            </div>
          ) : showParentPane ? (
            <div className="flex-1 flex min-h-0">
              {/* Parent pane (40%) — its header and empty area both step up
                  a level; folder rows still switch to that sibling. On a phone a
                  drive picker hides it: Places is a row of buttons there and the
                  path's ↑ does its job. */}
              <div className="flex w-2/5 min-h-0 border-r themed-border">
                <PickerColumn
                  header={readableName(shownStack[shownStack.length - 2].name)}
                  body={parentListPlace
                    ? <PlaceListView place={parentListPlace} currentFolderId={currentFolderId} selectedId={selectedId} hideEntry={hideEntry} onOpen={selectPlaceSibling} />
                    : undefined}
                  items={visibleParentItems}
                  loading={parentColLoading}
                  selectedId={selectedId}
                  currentId={currentFolderId}
                  emptyMessage={mode === 'folder' ? t('drivePicker.noFolders') : t('drivePicker.empty')}
                  decorateItem={propsRef.current.decorateItem}
                  parentPermission={parentColPermission}
                  checks={checks}
                  onRowClick={(item, perm) => item.isFolder && !rowChooses(item, perm) ? selectSibling(item) : handleRowClick(item, perm)}
                  // Opening a folder from this pane switches to that sibling.
                  onRowDoubleClick={(item, perm) => {
                    if (!item.isFolder) handleRowDoubleClick(item, perm)
                    else if (rowChooses(item, perm)) {
                      propsRef.current.onOpenByDoubleClick?.(selectionOf(item))
                      selectSibling(item)
                    }
                  }}
                  onRowOpen={selectSibling}
                  onHeaderClick={goUp}
                  onBackgroundClick={goUp}
                  className="flex-1 min-w-0"
                />
              </div>
              {/* Current pane (the rest) */}
              <PickerColumn
                header={readableName(shownStack[shownStack.length - 1].name)}
                items={visibleItems}
                loading={browseLoading}
                failed={browseFailed ? { onTryAgain: () => setBrowseAgain(n => n + 1) } : undefined}
                selectedId={selectedId}
                emptyMessage={mode === 'folder' ? t('drivePicker.noFoldersHere') : t('drivePicker.folderEmpty')}
                decorateItem={propsRef.current.decorateItem}
                parentPermission={parentPermission}
                checks={checks}
                onRowClick={handleRowClick}
                onRowDoubleClick={handleRowDoubleClick}
                onRowOpen={drillInto}
                className="flex-1 min-w-0"
              />
            </div>
          ) : (
            <PickerColumn
              items={visibleItems}
              loading={browseLoading}
              failed={browseFailed ? { onTryAgain: () => setBrowseAgain(n => n + 1) } : undefined}
              selectedId={selectedId}
              emptyMessage={mode === 'folder' ? t('drivePicker.noFoldersHere') : t('drivePicker.folderEmpty')}
              decorateItem={propsRef.current.decorateItem}
              parentPermission={parentPermission}
              checks={checks}
              onRowClick={handleRowClick}
              onRowDoubleClick={handleRowDoubleClick}
              onRowOpen={drillInto}
              className="flex-1"
            />
          )}
        </div>
      </div>
    )
  },
)

// ─── Sub-components ───────────────────────────────────────────────────

/** Themed hover tooltip — pure CSS via `group/tip` + `group-hover/tip:`,
 *  so it shows instantly (no native-title delay) and matches the modal's
 *  theme. Wraps the target in an `inline-flex` span; for layout-sensitive
 *  spots, pass a `className` override (e.g. `flex flex-1 min-w-0`).
 *  Defaults to appearing below the target; use `side="top"` near a
 *  bottom edge to flip. Exported for reuse from the dialog wrappers. */
export function Tooltip({
  children, label, side = 'bottom', className = 'inline-flex min-w-0',
}: {
  children: ReactNode
  label: string
  side?: 'top' | 'bottom'
  className?: string
}) {
  // Default also includes `min-w-0` so wrapping a truncating child
  // (e.g. a breadcrumb segment with `max-w-[140px] truncate`) inside a
  // flex parent still shrinks correctly.
  return (
    <span className={`group/tip relative ${className}`}>
      {children}
      <span
        role="tooltip"
        className={`pointer-events-none absolute left-1/2 -translate-x-1/2 z-50 hidden group-hover/tip:block whitespace-nowrap ${
          side === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
        }`}
      >
        <span className="themed-bg-secondary themed-border border rounded px-2 py-0.5 text-[11px] themed-text shadow-md">
          {label}
        </span>
      </span>
    </span>
  )
}

/** Breadcrumb trail with middle-truncation: when the path is long, the
 *  first crumb stays, the middle collapses into a `…` overflow menu, and
 *  the last two crumbs stay visible. Each segment is clickable to jump;
 *  clicking the bar's own empty area swaps it for a selectable readonly
 *  input of the full slash-path (Windows address-bar style); clicking
 *  `…` opens a dropdown of the hidden segments, parent-child indented. */
function Breadcrumbs({
  stack: crumbs, expanded, onToggleExpanded, overflowOpen, setOverflowOpen, onJump,
}: {
  stack: PickerCrumb[]
  expanded: boolean
  onToggleExpanded: () => void
  overflowOpen: boolean
  setOverflowOpen: (v: boolean) => void
  onJump: (index: number) => void
}) {
  const { t } = useTranslation('files')
  // The stack keeps real folder names; the trail shows them as a person reads them.
  const stack = crumbs.map(crumb => ({ ...crumb, name: readableName(crumb.name) }))
  const fullPath = '/' + stack.map(c => c.name).join('/')

  // Empty-area hover: shown only when the cursor is on the container itself
  // (not over a segment, the `…` button, or any of their children) — that
  // way the per-segment Tooltip and the full-path tooltip don't dual-fire.
  const [emptyAreaHovered, setEmptyAreaHovered] = useState(false)

  // Expanded — show the full path as selectable text; blur collapses back.
  if (expanded) {
    return (
      <input
        readOnly
        autoFocus
        value={fullPath}
        title={fullPath}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={onToggleExpanded}
        className="w-full text-xs themed-text bg-transparent outline-none px-1 py-0.5"
      />
    )
  }

  // Fires only when the click lands on the bar's own area — not a segment
  // button, the `…` menu, or their children — so it can't hijack a jump.
  const expandOnEmptyClick = (e: { target: EventTarget; currentTarget: EventTarget }) => {
    if (e.target === e.currentTarget) onToggleExpanded()
  }

  const MAX_VISIBLE = 4
  const crumbBtn = (crumb: PickerCrumb, index: number, isLast: boolean) => (
    <Tooltip key={crumb.id + index} label={crumb.name}>
      <button
        type="button"
        onClick={() => !isLast && onJump(index)}
        disabled={isLast}
        aria-label={crumb.name}
        className={`px-1 py-0.5 rounded truncate max-w-[140px] transition-colors ${
          isLast
            ? 'themed-text font-medium cursor-default'
            : 'themed-text-secondary hover:themed-text hover:themed-bg-tertiary'
        }`}
      >
        {crumb.name}
      </button>
    </Tooltip>
  )
  const sep = (key: string) => (
    <ChevronRight key={key} size={12} className="shrink-0 themed-text-muted" />
  )

  // Hint tooltip — rendered only when the cursor is on the container's
  // own area. Tells the user that clicking will reveal the full path.
  const emptyAreaTooltip = (
    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1 z-50 whitespace-nowrap">
      <span className="themed-bg-secondary themed-border border rounded px-2 py-0.5 text-[11px] themed-text shadow-md">
        {t('drivePicker.fullPathHint')}
      </span>
    </span>
  )

  if (stack.length <= MAX_VISIBLE) {
    return (
      <div
        data-testid="picker-breadcrumb"
        className="relative flex items-center gap-0.5 min-w-0 cursor-text"
        onClick={expandOnEmptyClick}
        onPointerMove={(e) => { if (isMouseHover(e)) setEmptyAreaHovered(e.target === e.currentTarget) }}
        onPointerLeave={(e) => { if (isMouseHover(e)) setEmptyAreaHovered(false) }}
      >
        {stack.map((crumb, i) => (
          <span key={crumb.id + i} className="flex items-center gap-0.5 min-w-0">
            {i > 0 && sep(`s${i}`)}
            {crumbBtn(crumb, i, i === stack.length - 1)}
          </span>
        ))}
        {emptyAreaHovered && emptyAreaTooltip}
      </div>
    )
  }

  // first  …  secondLast  last
  const first = stack[0]
  const hidden = stack.slice(1, stack.length - 2)
  const tail = stack.slice(stack.length - 2)
  return (
    <div
      className="relative flex items-center gap-0.5 min-w-0 cursor-text"
      onClick={expandOnEmptyClick}
      onPointerMove={(e) => { if (isMouseHover(e)) setEmptyAreaHovered(e.target === e.currentTarget) }}
      onPointerLeave={(e) => { if (isMouseHover(e)) setEmptyAreaHovered(false) }}
    >
      {crumbBtn(first, 0, false)}
      {sep('s-first')}
      <div className="relative shrink-0">
        <Tooltip label={t('drivePicker.hiddenSegments')}>
          <button
            type="button"
            onClick={() => setOverflowOpen(!overflowOpen)}
            aria-label={t('drivePicker.hiddenSegments')}
            className="px-1 py-0.5 rounded themed-text-secondary hover:themed-text hover:themed-bg-tertiary transition-colors"
          >
            <MoreHorizontal size={13} />
          </button>
        </Tooltip>
        {overflowOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOverflowOpen(false)} />
            {/* Dropdown of hidden segments, parent → child indented.
                No `overflow-auto` — the per-item papan Tooltip needs to
                escape the dropdown's box, which `overflow-auto` would
                clip. With typical paths <10 hidden segments the natural
                dropdown height is small enough. */}
            <div className="absolute top-full left-0 mt-1 z-40 themed-bg-secondary themed-border border rounded shadow-lg py-1 min-w-[180px]">
              {hidden.map((crumb, i) => (
                /* Tooltip wrapper made block-level so the inner full-width
                   button fills the dropdown row. */
                <Tooltip key={crumb.id + i} label={crumb.name} className="flex w-full min-w-0">
                  <button
                    type="button"
                    onClick={() => onJump(i + 1)}
                    aria-label={crumb.name}
                    className="flex items-center w-full text-left text-xs themed-text-secondary hover:themed-text hover:themed-bg-tertiary py-1 pr-3"
                    style={{ paddingLeft: `${0.5 + i * 0.5}rem` }}
                  >
                    <span className="truncate">{crumb.name}</span>
                  </button>
                </Tooltip>
              ))}
            </div>
          </>
        )}
      </div>
      {tail.map((crumb, i) => {
        const realIndex = stack.length - 2 + i
        return (
          <span key={crumb.id + realIndex} className="flex items-center gap-0.5 min-w-0">
            {sep(`s-tail${i}`)}
            {crumbBtn(crumb, realIndex, realIndex === stack.length - 1)}
          </span>
        )
      })}
      {emptyAreaHovered && emptyAreaTooltip}
    </div>
  )
}

/**
 * A row that may not be chosen reads as such as a WHOLE, in its ending's tone:
 * the name and the icon (greyed — a type's colour left on it, a yellow CSV,
 * looks choosable) fade to the ending's opacity (`.file-ext-muted`, 0.25), and
 * the ending is not faded a second time inside it (nested opacity multiplies).
 */
const DIMMED_TEXT = 'opacity-25 [&_.file-ext-muted]:opacity-100 [&_.file-ext-muted-2]:opacity-60'
function DimmableIcon({ item, dim }: { item: FileListItem; dim: boolean }) {
  const icon = <FileIcon {...fileIconProps(item)} {...miniappIconProps(item)} />
  return dim ? <span className="flex shrink-0 opacity-25 grayscale" data-dimmed="">{icon}</span> : icon
}

/** A single file/folder row in the browse list. */
function PickerRow({
  item, selected, current, readOnly, dimReason: dimReasonProp, check, pressed, onOpen, onClick, onDoubleClick,
}: {
  item: FileListItem
  selected: boolean
  current: boolean
  readOnly?: boolean
  /** This row may not be chosen (or saved into), and why — dimmed, said on hover. */
  dimReason?: string
  /** Checkbox mode: the row's tick, or a spacer keeping rows aligned. */
  check?: RowCheck
  /** `checkStyle: 'row'`: the row IS the choice — whether it is chosen. */
  pressed?: boolean
  /** `checkStyle: 'row'`, a folder a click chooses: how to open it instead. */
  onOpen?: () => void
  onClick: () => void
  onDoubleClick: () => void
}) {
  const { t } = useTranslation('files')
  // D24: a `.chain` that Needs approval never opens or is chosen here — say why.
  const dimReason = dimReasonProp ?? (isPendingPointer(item) ? t('driveView.chain.needsApproval') : undefined)
  const row = (
    <PickerRowButton
      selected={selected}
      current={current}
      pressed={pressed}
      toggles={!item.isFolder && !!check && check !== 'spacer'}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <DimmableIcon item={item} dim={!!dimReason} />
      <span
        className={`truncate flex-1 ${dimReason ? DIMMED_TEXT : readOnly ? 'opacity-50' : ''}`}
        title={dimReason ? `${item.name} — ${dimReason}` : item.name}
      >
        <RowName item={item} />
        {/* The hover title reaches no keyboard or screen-reader user: say it here too. */}
        {dimReason && <span className="sr-only">{` — ${dimReason}`}</span>}
      </span>
      {readOnly && <span className="text-[10px] themed-text-muted shrink-0">{t('drivePicker.readOnly')}</span>}
      {item.isFolder && !onOpen && <ChevronRight size={14} className="shrink-0 themed-text-muted" />}
    </PickerRowButton>
  )
  if (onOpen) return <OpenableRow label={item.name} onOpen={onOpen}>{row}</OpenableRow>
  return check ? <CheckedRow check={check} label={item.name}>{row}</CheckedRow> : row
}

/**
 * `checkStyle: 'row'`: a folder whose click CHOOSES it, with its open button
 * BESIDE the row — never inside it (a control nested in a button is invalid
 * and unreachable by keyboard).
 */
function OpenableRow({ label, onOpen, children }: { label: string; onOpen: () => void; children: ReactNode }) {
  const { t } = useTranslation('files')
  return (
    <div className="flex items-center gap-0.5">
      <div className="flex-1 min-w-0">{children}</div>
      <button
        type="button"
        aria-label={t('drivePicker.openFolder', { name: label })}
        title={t('drivePicker.openFolder', { name: label })}
        // Its own gesture: never also a click on the row (which chooses it).
        onClick={e => { e.stopPropagation(); onOpen() }}
        onDoubleClick={e => e.stopPropagation()}
        className="shrink-0 p-1.5 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  )
}

/** Checkbox mode: a row's tick (`checked` + how to toggle it), or a spacer. */
type RowCheck = { checked: boolean; onToggle: () => void } | 'spacer'

/**
 * A row with its checkbox BESIDE it — never inside: the row is a button, and a
 * control nested in a button is invalid and unreachable by keyboard.
 */
function CheckedRow({ check, label, children }: { check: RowCheck; label: string; children: ReactNode }) {
  const { t } = useTranslation('files')
  return (
    <div className="flex items-center gap-1">
      {check === 'spacer' ? (
        <span className="w-4 mx-0.5 shrink-0" aria-hidden />
      ) : (
        <button
          type="button"
          role="checkbox"
          aria-checked={check.checked}
          aria-label={t('drivePicker.choose', { name: label })}
          // Its own gesture: never also a click on the row (which may open a folder).
          onClick={e => { e.stopPropagation(); check.onToggle() }}
          // Space ticks, as on a native checkbox; Enter never does — it is the
          // dialog's (a picker confirms on it: toggling first would untick the
          // very choice being confirmed).
          onKeyDown={e => { if (e.key === 'Enter') e.preventDefault() }}
          onDoubleClick={e => e.stopPropagation()}
          // Wherever there is a touch screen — a touch laptop too, whose main
          // pointer is its trackpad — the tick takes a finger-sized tap around
          // it (unseen): a folder is chosen only by its tick, its row opens it.
          className={`relative shrink-0 w-4 h-4 mx-0.5 rounded border-[1.5px] flex items-center justify-center transition-colors any-pointer-coarse:before:absolute any-pointer-coarse:before:-inset-2 any-pointer-coarse:before:content-[''] ${
            // Unticked: the theme's muted text colour, not its faint border —
            // a box that reads as a box on every theme.
            check.checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-[var(--text-muted)] hover:border-blue-500'
          }`}
        >
          {check.checked && <Check size={11} strokeWidth={3} />}
        </button>
      )}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}

/** A row's check + dimming in checkbox mode, from the wrapper's `rowChoice`. */
function rowCheckState(item: FileListItem, checks: ColumnChecks | undefined, parentPermission?: string) {
  if (!checks) return { check: undefined, dimReason: undefined, pressed: undefined, chooses: false }
  const choice = checks.choiceOf(item, parentPermission)
  const dimReason = typeof choice === 'string' ? choice : undefined
  if (checks.rows) {
    // No tick: the row itself is the choice.
    return { check: undefined, dimReason, pressed: choice === true ? checks.checkedIds.has(item.id) : undefined, chooses: choice === true }
  }
  const check: RowCheck = choice === true
    ? { checked: checks.checkedIds.has(item.id), onToggle: () => checks.onToggle(item, parentPermission) }
    : 'spacer'
  return { check, dimReason, pressed: undefined, chooses: false }
}

/** One column of the browse view — an optional header plus a scrollable
 *  file/folder list. Rendered once in single-column mode, twice (parent |
 *  current) in split view. A single `selectedId` drives the selection
 *  highlight, so exactly one row is ever selected across both panes; the
 *  parent pane also marks the folder you are in (`currentId`). */
function PickerColumn({
  header, body, items, loading, failed, selectedId, currentId = null, emptyMessage,
  decorateItem, parentPermission, checks, onRowClick, onRowDoubleClick, onRowOpen,
  onHeaderClick, onBackgroundClick, className,
}: {
  header?: string
  /** Replaces the folder rows (the parent pane showing a place's list). */
  body?: ReactNode
  items: FileListItem[]
  loading: boolean
  /** The listing failed: say so, with Try again (never "empty"). */
  failed?: { onTryAgain: () => void }
  selectedId: string | null
  /** The open folder, when this column lists its siblings (the parent pane). */
  currentId?: string | null
  emptyMessage: string
  decorateItem?: (item: FileListItem, parentPermission?: string) => RowDecoration | undefined
  parentPermission?: string
  /** Checkbox mode (see `PickerNavigatorProps.checkedIds`). */
  checks?: ColumnChecks
  onRowClick: (item: FileListItem, parentPermission?: string) => void
  onRowDoubleClick: (item: FileListItem, parentPermission?: string) => void
  /** `checkStyle: 'row'`: open a folder a click would choose (its open button). */
  onRowOpen?: (item: FileListItem) => void
  /** When set, the header renders as a "go up a level" button — used for
   *  the split view's parent pane. */
  onHeaderClick?: () => void
  /** When set, clicking the pane's empty space (anywhere outside a row)
   *  fires this — also "go up a level" for the parent pane. Rows
   *  `stopPropagation`, so only non-row clicks reach it. */
  onBackgroundClick?: () => void
  className?: string
}) {
  const { t } = useTranslation('files')
  return (
    <div className={`flex flex-col min-h-0 ${className ?? ''}`}>
      {header && (
        onHeaderClick ? (
          <Tooltip label={t('drivePicker.backTo', { name: header })}>
            <button
              type="button"
              // One step up per gesture: after the first, this header names the next parent.
              onClick={e => { if (e.detail <= 1) onHeaderClick() }}
              aria-label={t('drivePicker.backTo', { name: header })}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium themed-text-muted hover:themed-text hover:themed-bg-tertiary truncate shrink-0 text-left transition-colors"
            >
              <ArrowLeft size={11} className="shrink-0" />
              <span className="truncate">{header}</span>
            </button>
          </Tooltip>
        ) : (
          <div className="px-2.5 py-1 text-[11px] font-medium themed-text-muted truncate shrink-0">
            {header}
          </div>
        )
      )}
      <div
        className="flex-1 overflow-auto p-1.5 min-h-0"
        data-testid="picker-list"
        // The pane's own empty space only — not a section label or status text in
        // it — and one step up per gesture: a double-click's second click is not another.
        onClick={onBackgroundClick && (e => { if (e.target === e.currentTarget && e.detail <= 1) onBackgroundClick() })}
      >
        {body ?? (loading ? (
          <CenteredSpinner />
        ) : failed ? (
          <LoadFailed onTryAgain={failed.onTryAgain} />
        ) : items.length === 0 ? (
          <EmptyState message={emptyMessage} />
        ) : (
          items.map(item => {
            const deco = decorateItem?.(item, parentPermission)
            const { check, dimReason, pressed, chooses } = rowCheckState(item, checks, parentPermission)
            return (
              <PickerRow
                key={item.id}
                item={item}
                selected={checks ? checks.checkedIds.has(item.id) : item.id === selectedId}
                current={item.id === currentId}
                readOnly={deco?.readOnly}
                dimReason={dimReason ?? deco?.dimReason}
                check={check}
                pressed={pressed}
                onOpen={chooses && item.isFolder && onRowOpen ? () => onRowOpen(item) : undefined}
                onClick={() => onRowClick(item, parentPermission)}
                onDoubleClick={() => onRowDoubleClick(item, parentPermission)}
              />
            )
          })
        ))}
      </div>
    </div>
  )
}

/** Search results list — each row shows the name plus where it is. */
function SearchResultsView({
  loading, results, resultPaths, selectedId, checks, onClickResult, onOpenResult,
}: {
  loading: boolean
  results: FileListItem[] | null
  /** Parent folder id → readable location. */
  resultPaths: Map<string, string>
  selectedId: string | null
  /** Checkbox mode (see `PickerNavigatorProps.checkedIds`). */
  checks?: ColumnChecks
  onClickResult: (item: FileListItem) => void
  /** `checkStyle: 'row'`: open a folder hit a click would choose. */
  onOpenResult?: (item: FileListItem) => void
}) {
  const { t } = useTranslation('files')
  if (loading && !results) return <CenteredSpinner />
  if (!results || results.length === 0) {
    return <EmptyState message={t('drivePicker.noMatches')} />
  }
  return (
    <>
      {results.map(item => {
        const path = item.parentId ? resultPaths.get(item.parentId) : ''
        const state = rowCheckState(item, checks)
        const { check, pressed, chooses } = state
        const dimReason = state.dimReason ?? (isPendingPointer(item) ? t('driveView.chain.needsApproval') : undefined)
        const selected = checks ? checks.checkedIds.has(item.id) : item.id === selectedId
        const openable = chooses && item.isFolder && !!onOpenResult
        const hit = (
          <button
            key={item.id}
            type="button"
            aria-pressed={pressed}
            // A double-click's second click would undo its first (see PickerRowButton) —
            // a hit that is a choice, or a file hit whose click ticks it.
            onClick={(e) => { if (!((pressed !== undefined || (!item.isFolder && !!check && check !== 'spacer')) && isSecondClickOnSelf(e))) onClickResult(item) }}
            className={`flex items-center gap-2 w-full text-left py-1.5 px-2 rounded text-sm transition-colors ${
              selected
                ? 'bg-blue-600/30 themed-text'
                : 'themed-text-secondary hover:themed-text hover:themed-bg-tertiary'
            }`}
          >
            <DimmableIcon item={item} dim={!!dimReason} />
            <span className={`flex flex-col min-w-0 flex-1 ${dimReason ? DIMMED_TEXT : ''}`}>
              <span className="truncate" title={dimReason ? `${item.name} — ${dimReason}` : item.name}>
                <RowName item={item} />
                {dimReason && <span className="sr-only">{` — ${dimReason}`}</span>}
              </span>
              {path && <span className="truncate text-[10px] themed-text-muted">{path}</span>}
            </span>
            {item.isFolder && !openable && <ChevronRight size={14} className="shrink-0 themed-text-muted" />}
          </button>
        )
        if (openable) return <OpenableRow key={item.id} label={item.name} onOpen={() => onOpenResult!(item)}>{hit}</OpenableRow>
        return check ? <CheckedRow key={item.id} check={check} label={item.name}>{hit}</CheckedRow> : hit
      })}
    </>
  )
}

/** Root chooser — shown as level 0 when the navigator has multiple roots. */
function RootChooserView({
  roots, onChoose,
}: {
  roots: PickerRoot[]
  onChoose: (root: PickerRoot) => void
}) {
  const { t } = useTranslation('files')
  if (roots.length === 0) return <EmptyState message={t('drivePicker.nothingToBrowse')} />
  return (
    <>
      {roots.map(root => (
        <button
          key={root.id}
          type="button"
          onClick={() => onChoose(root)}
          className="flex items-center gap-2 w-full text-left py-1.5 px-2 rounded text-sm themed-text-secondary hover:themed-text hover:themed-bg-tertiary transition-colors"
        >
          <FileIcon isFolder isLink={root.isLink} />
          <span className="truncate flex-1">{root.label}</span>
          <ChevronRight size={14} className="shrink-0 themed-text-muted" />
        </button>
      ))}
    </>
  )
}

function CenteredSpinner() {
  return (
    <div className="flex items-center justify-center p-6">
      <Loader2 size={16} className="animate-spin themed-text-muted" />
    </div>
  )
}

/** A listing that failed — never read as an empty folder. */
function LoadFailed({ onTryAgain }: { onTryAgain: () => void }) {
  const { t } = useTranslation('files')
  return (
    <div className="text-sm themed-text-muted text-center py-10" data-testid="picker-load-failed">
      {t('drivePicker.folderLoadFailed')}{' '}
      <button type="button" onClick={onTryAgain} className="underline hover:themed-text">{t('drivePicker.retry')}</button>
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return <div className="text-sm themed-text-muted text-center py-10">{message}</div>
}
